import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowsInSimple,
  ArrowSquareOut,
  CaretLeft,
  CaretRight,
  CornersOut,
  Lock,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Pause,
  Play,
  SpeakerSimpleHigh,
  SpeakerSimpleSlash,
  ThumbsDown,
  ThumbsUp,
} from "@phosphor-icons/react";
import {
  Button,
  Flex,
  IconButton,
  Slider,
  Spinner,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import Hls from "hls.js";
import { getDocument, type RenderTask } from "pdfjs-dist";
import {
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ImageCommentPin } from "./ImageCommentPin";
import {
  type Rotation,
  inverseRotatePoint,
  nextRotation,
  rotatePoint,
} from "./rotationUtils";

import { getSignedPdfUrl } from "../../../services/files";
import { renderReviewCommentAvatar } from "../../../utils/helpers/ai-persona-avatars";
import { useEditCommentMutation } from "../../../utils/queries/projects";
import { formatTimeStamp } from "../../tickets/components/VideoPlayback";

export interface AssetFile {
  source: string;
  name?: string;
  contentType?: string;
}

/**
 * Zoom bounds shared by `PdfViewer` and `ImageViewer`. Hoisted to file
 * scope so wheel-zoom, keyboard +/-, and the explicit zoom buttons can
 * never drift apart — every cap funnels through `clampZoom`.
 *
 * Why 10x (1000%) as the ceiling:
 *   - Packshot reviewers (brand + regulatory) need to inspect sub-pixel
 *     artifacts on small back-panel text and barcode swatches; 300% (and
 *     even the interim 600% from #3461) was not enough for that use case
 *     and people complained.
 *   - PDFs render through pdf.js at `1.5 * zoom` device scale, so 10x =
 *     a 15x render scale. That's the practical upper bound where the
 *     re-render stays under ~500ms on typical hardware and image data
 *     is still meaningful before pixel-quad blur dominates.
 *
 * If a reviewer asks for higher: bump `MAX_ZOOM` only — every code path
 * already reads from it.
 */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.25;

/** Clamp a zoom level to the shared `[MIN_ZOOM, MAX_ZOOM]` interval. */
function clampZoom(z: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

// Track which orphan-page comments we've already warned about so a single
// missing-page_num doesn't spam the console as the comments list re-polls.
const orphanPageWarnedIds = new Set<string>();

function useOrphanPagePinWarning(comments?: BaseTicketComment[]) {
  useEffect(() => {
    if (!comments?.length) return;
    for (const c of comments) {
      const id = c?._id != null ? String(c._id) : null;
      if (!id) continue;
      if (c.page_num != null) continue;
      if (orphanPageWarnedIds.has(id)) continue;
      orphanPageWarnedIds.add(id);
      // Renderer falls back to page 1, but that's a backend bug — the
      // pin will land on the wrong page silently. Log so we can spot it
      // in dev/Datadog instead of waiting for a customer to report.
      console.warn(
        "[AskPuntt] Comment rendered with no page_num; falling back to page 1.",
        { commentId: id },
      );
    }
  }, [comments]);
}

interface HighlightPosition {
  x: number;
  y: number;
  page?: number;
}

interface AssetViewerPaneProps {
  title: string;
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  reviewStatus?: string | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onPinClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (
    pageNumber: number,
    position?: { x: number; y: number },
    sourceUrl?: string,
    originalURL?: string,
  ) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onCollapse?: () => void;
  onBackToProject?: () => void;
  ticketId?: string;
}

export interface AssetViewerPaneHandle {
  navigateToPage: (page: number) => void;
  // `pendingPage` lands on the newly-activated file once its viewer mounts,
  // even before the document has finished loading. Required because
  // navigateToFile's setActiveTab is deferred; a synchronous navigateToPage
  // fired right after it would hit the outgoing viewer's setter.
  navigateToFile: (fileIndex: number, pendingPage?: number) => void;
}

function isPdf(asset: AssetFile): boolean {
  if (asset.contentType?.includes("pdf")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(pdf|ai)(\?|$)/.test(ext);
}

function isImage(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("image/")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/.test(ext);
}

// Spreadsheet files are rendered through the PDF pipeline by the backend
// (one giant page per sheet). Route them to PdfViewer so comment pins and
// page navigation work, same as docx/pptx after the office-to-pdf conversion.
function isSpreadsheet(asset: AssetFile): boolean {
  const ext = (asset.name ?? asset.source).toLowerCase();
  if (/\.(xls|xlsx|csv|ods)(\?|$)/.test(ext)) return true;
  const ct = asset.contentType ?? "";
  return (
    ct.includes("spreadsheetml") || ct.includes("ms-excel") || ct === "text/csv"
  );
}

function isOfficeFile(asset: AssetFile): boolean {
  // Spreadsheets are handled separately by isSpreadsheet — they render via
  // the PDF pipeline and must NOT fall through to the OfficeViewer iframe.
  if (isSpreadsheet(asset)) return false;
  const ext = (asset.name ?? asset.source).toLowerCase();
  if (/\.(ppt|pptx|doc|docx)(\?|$)/.test(ext)) return true;
  const ct = asset.contentType ?? "";
  return (
    ct.includes("presentationml") ||
    ct.includes("wordprocessingml") ||
    ct.includes("msword") ||
    ct.includes("ms-powerpoint")
  );
}

function isVideo(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("video/")) return true;
  if (
    asset.contentType === "application/vnd.apple.mpegurl" ||
    asset.contentType === "application/x-mpegurl"
  )
    return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(mp4|mov|avi|wmv|flv|mkv|m3u8)(\?|$)/.test(ext);
}

// ---------------------------------------------------------------------------
// CommentPanel -- single-comment navigation view (replaces CommentStrip)
//
// Intentionally read-only: reviewers asked us not to support @puntt or a
// dedicated reply thread here because the chat agent already has access to
// these comments. Any "ask about this comment" affordance should live as a
// future, chat-driven entry point.
// ---------------------------------------------------------------------------

function CommentPanel({
  comments,
  activeCommentId,
  onCommentClick,
  onCitationClick,
  onNavigateToPage,
  ticketId,
}: {
  comments: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (
    pageNumber: number,
    position?: { x: number; y: number },
    sourceUrl?: string,
    originalURL?: string,
  ) => void;
  onNavigateToPage?: (page: number) => void;
  ticketId?: string;
}) {
  // Track index internally so the 3-second highlight auto-clear doesn't
  // reset the panel back to comment 0.
  const [internalIndex, setInternalIndex] = useState(0);

  useEffect(() => {
    if (!activeCommentId) return;
    const idx = comments.findIndex((c) => c._id === activeCommentId);
    if (idx >= 0) setInternalIndex(idx);
  }, [activeCommentId, comments]);

  const currentIndex = Math.min(
    internalIndex,
    Math.max(comments.length - 1, 0),
  );
  const comment = comments[currentIndex];

  const editCommentMutation = useEditCommentMutation();
  const [liked, setLiked] = useState<boolean | null>(comment?.liked ?? null);

  useEffect(() => {
    setLiked(comment?.liked ?? null);
  }, [comment?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleThumbsUp() {
    if (!ticketId || !comment?._id || editCommentMutation.isPending) return;
    const newLiked = liked === true ? null : true;
    setLiked(newLiked);
    editCommentMutation.mutateAsync({
      ticketId,
      messageId: comment._id,
      payload: { description: comment.description ?? "", liked: newLiked },
    });
  }

  function handleThumbsDown() {
    if (!ticketId || !comment?._id || editCommentMutation.isPending) return;
    const newLiked = liked === false ? undefined : false;
    setLiked(newLiked ?? null);
    const payload: { description: string; liked?: false } = {
      description: comment.description ?? "",
    };
    if (newLiked !== undefined) payload.liked = newLiked;
    editCommentMutation.mutateAsync({
      ticketId,
      messageId: comment._id,
      payload,
    });
  }

  const fileCitations = useMemo(() => {
    if (!comment) return [];
    return (Array.isArray(comment.citations) ? comment.citations : []).filter(
      (
        c,
      ): c is {
        url: string;
        pageNumber?: number;
        originalURL?: string;
        rel_x?: number;
        rel_y?: number;
      } => "originalURL" in c || "rel_x" in c,
    );
  }, [comment]);

  if (!comment) return null;

  const navigate = (dir: 1 | -1) => {
    const nextIdx = currentIndex + dir;
    const next = comments[nextIdx];
    if (next) {
      setInternalIndex(nextIdx);
      onCommentClick?.(next);
      if (next.page_num != null) {
        onNavigateToPage?.(next.page_num);
      }
    }
  };

  return (
    <div
      data-testid="asset-viewer-comment-panel"
      className="shrink-0 border-t border-puntt-neutral-gray-6 bg-base-white/90 backdrop-blur-sm"
    >
      {/* Navigation header - height locked to match ViewerToolbar so the two
          bars render as a single continuous piece of chrome. */}
      <Flex
        align="center"
        justify="between"
        className="h-10 border-b border-puntt-neutral-gray-6 px-3"
      >
        <Text
          size="1"
          weight="medium"
          data-testid="comment-panel-counter"
          className="uppercase tracking-wide text-puntt-neutral-gray-9"
        >
          {currentIndex + 1}/{comments.length} Comments
          {comment.page_num != null && (
            <span className="ml-1 normal-case text-puntt-neutral-gray-8">
              · Page {comment.page_num}
            </span>
          )}
          {comment.cellAnchor != null && (
            <span
              data-testid="comment-panel-cell-anchor-badge"
              className="ml-1 normal-case text-puntt-neutral-gray-8"
            >
              · {comment.cellAnchor.sheetName} {comment.cellAnchor.range}
            </span>
          )}
        </Text>
        <Flex gap="1">
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentIndex === 0}
            onClick={() => navigate(-1)}
            aria-label="Previous comment"
            data-testid="comment-panel-prev"
          >
            <ArrowLeft size={14} />
          </IconButton>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentIndex === comments.length - 1}
            onClick={() => navigate(1)}
            aria-label="Next comment"
            data-testid="comment-panel-next"
          >
            <ArrowRight size={14} />
          </IconButton>
        </Flex>
      </Flex>

      {/* Comment card */}
      <div className="space-y-3 p-4">
        <Flex gap="2" align="center">
          {renderReviewCommentAvatar(comment)}
          <Text size="3" weight="medium" className="text-puntt-neutral-gray-12">
            {comment.rule ?? "Comment"}
          </Text>
        </Flex>

        <Text
          as="div"
          size="2"
          data-testid="comment-panel-body"
          className="leading-relaxed text-puntt-neutral-gray-12"
        >
          {comment.description}
        </Text>

        {fileCitations.length > 0 && (
          <Flex gap="2" wrap="wrap">
            {fileCitations.map((citation, idx) => {
              const page = citation.pageNumber ?? 1;
              const pos =
                citation.rel_x != null && citation.rel_y != null
                  ? { x: citation.rel_x, y: citation.rel_y }
                  : undefined;
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid="comment-panel-citation"
                  onClick={() =>
                    onCitationClick?.(
                      page,
                      pos,
                      citation.url,
                      citation.originalURL,
                    )
                  }
                  className="inline-flex cursor-pointer items-center gap-1 text-puntt-accent-11 underline-offset-2 hover:underline"
                  aria-label={`Navigate to source on page ${page}`}
                >
                  <Text as="span" size="1" weight="medium">
                    Page {page}
                  </Text>
                  <ArrowSquareOut size={12} />
                </button>
              );
            })}
          </Flex>
        )}

        {comment.isAI && ticketId && (
          <Flex gap="1" justify="end">
            <Tooltip
              content={liked === true ? "Remove thumbs up" : "Thumbs up"}
            >
              <IconButton
                data-testid="comment-panel-thumbs-up"
                data-auth-trigger="thumbs-up"
                size="1"
                variant="soft"
                color={liked === true ? "green" : undefined}
                onClick={handleThumbsUp}
              >
                <ThumbsUp
                  weight={liked === true ? "fill" : "regular"}
                  height="16"
                  width="16"
                />
              </IconButton>
            </Tooltip>
            <Tooltip
              content={liked === false ? "Remove thumbs down" : "Thumbs down"}
            >
              <IconButton
                data-testid="comment-panel-thumbs-down"
                data-auth-trigger="thumbs-down"
                size="1"
                variant="soft"
                color={liked === false ? "red" : undefined}
                onClick={handleThumbsDown}
              >
                <ThumbsDown
                  weight={liked === false ? "fill" : "regular"}
                  height="16"
                  width="16"
                />
              </IconButton>
            </Tooltip>
          </Flex>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Office file viewer via Microsoft public embed
// ---------------------------------------------------------------------------

function OfficeViewer({ url }: { url: string }) {
  // Microsoft's view.officeapps.live.com fetches the file from its own
  // servers, so the `src` URL must be publicly reachable. Our canonical
  // ``static.puntt.ai`` CDN URLs are gated by a CloudFront x-api-key Lambda
  // and return 401 to outside callers. Exchange the CDN URL for a short-
  // lived presigned S3 GET URL before handing it to Microsoft.
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setPublicUrl(null);
    getSignedPdfUrl(url)
      .then((u) => {
        if (!cancelled) setPublicUrl(u);
      })
      .catch(() => {
        if (!cancelled) setPublicUrl(url);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!publicUrl) {
    return (
      <div
        data-testid="office-viewer-loading"
        className="flex size-full items-center justify-center bg-base-white"
      >
        <Spinner size="3" />
      </div>
    );
  }

  return (
    <iframe
      data-testid="office-viewer-iframe"
      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(publicUrl)}`}
      className="size-full border-0"
      title="Office document preview"
    />
  );
}

// ---------------------------------------------------------------------------
// VideoViewer -- video player with HLS, seek bar, and comment timestamp pins
// ---------------------------------------------------------------------------

function VideoViewer({
  url,
  comments,
  activeCommentId,
  onPinClick,
  reviewStatus,
}: {
  url: string;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  /**
   * Optional processing status forwarded from the parent. When it's set
   * and not "Review complete", we show a lightweight "Preparing video…"
   * placeholder instead of the player because the HLS playlist may not
   * yet be resolvable — the review pipeline is still transcoding.
   */
  reviewStatus?: string | null;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  const stillProcessing =
    reviewStatus != null && reviewStatus !== "Review complete";

  const videoComments = useMemo(
    () => (comments ?? []).filter((c) => c.videoStart != null),
    [comments],
  );

  // Attach hls.js + track duration. Mirrors the ticket's `VideoMode`
  // setup intentionally — that player has been shipping the straight
  // `<video src> + hls.attachMedia` pattern without the failure modes
  // we kept hitting with the custom retry/error hook.
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !url || stillProcessing) return;

    const handleLoadedMetadata = () => {
      if (videoElement.duration && Number.isFinite(videoElement.duration)) {
        setDuration(videoElement.duration);
      }
    };
    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("durationchange", handleLoadedMetadata);

    let hls: Hls | null = null;
    const isHlsManifest = /\.m3u8(\?|$)/i.test(url);
    if (isHlsManifest && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(videoElement);
    }

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.removeEventListener("durationchange", handleLoadedMetadata);
      hls?.destroy();
    };
  }, [url, stillProcessing]);

  // Set initial volume on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seek to active comment's timestamp
  useEffect(() => {
    if (!activeCommentId || !videoRef.current) return;
    const comment = videoComments.find((c) => c._id === activeCommentId);
    if (comment?.videoStart != null) {
      videoRef.current.currentTime = comment.videoStart;
      videoRef.current.pause();
      setIsPlaying(false);
      setProgress(duration > 0 ? (comment.videoStart / duration) * 100 : 0);
    }
  }, [activeCommentId, videoComments, duration]);

  // Pause on tab switch / visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Pause on URL change (asset switch)
  useEffect(() => {
    return () => {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    };
  }, [url]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const pct = (video.currentTime / video.duration) * 100;
    setProgress(pct);
    if (pct >= 100) setIsPlaying(false);
  };

  const handleSeek = (value: number[]) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const seekTime = (value[0] / 100) * video.duration;
    video.currentTime = seekTime;
    setProgress(value[0]);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
    if (videoRef.current) {
      videoRef.current.volume = value[0];
      setIsMuted(value[0] === 0);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (stillProcessing) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-3 p-8"
        data-testid="asset-viewer-video-processing"
      >
        <Spinner size="3" />
        <Text size="2" className="text-puntt-neutral-gray-11">
          Preparing video preview…
        </Text>
        <Text size="1" className="text-puntt-neutral-gray-9">
          {reviewStatus}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Video area */}
      <div
        data-testid="asset-viewer-pane-div-615"
        role="button"
        tabIndex={0}
        className="relative flex min-h-0 flex-1 cursor-pointer items-center justify-center bg-puntt-neutral-gray-3"
        onClick={togglePlayPause}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            togglePlayPause();
          }
        }}
      >
        {/* eslint-disable jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          src={url}
          className="max-h-full max-w-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          playsInline
        />
        {!isPlaying && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-puntt-neutral-gray-10/75 p-3">
            <Play className="text-white" size={28} weight="fill" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="shrink-0 border-t border-puntt-neutral-gray-6 bg-base-white">
        {/* Seek bar with comment pins */}
        <div className="relative px-3 pt-2">
          <Slider
            variant="classic"
            size="1"
            min={0}
            max={100}
            step={0.1}
            value={[progress]}
            onValueChange={handleSeek}
            className="z-[1] cursor-pointer [--cursor-slider-thumb:pointer] [--radius-factor:0]"
          />
          {/* Comment pins overlay */}
          <div className="pointer-events-none absolute inset-x-3 top-0 h-5">
            {videoComments.map((comment) => {
              const left =
                duration > 0 ? ((comment.videoStart ?? 0) / duration) * 100 : 0;
              return (
                <button
                  data-testid="asset-viewer-pane-button-662"
                  key={comment._id}
                  className={`pointer-events-auto absolute top-0 z-10 size-2.5 -translate-x-1/2 rounded-full transition-transform ${
                    comment._id === activeCommentId
                      ? "scale-150 bg-puntt-accent-9"
                      : "bg-puntt-gold-9 hover:scale-125"
                  }`}
                  style={{ left: `${left}%` }}
                  title={`${formatTimeStamp(comment.videoStart)} — ${comment.description?.slice(0, 60) ?? ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPinClick?.(comment);
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Transport controls */}
        <Flex align="center" justify="between" className="px-3 py-1.5">
          <Flex align="center" gap="2">
            <IconButton
              data-testid="asset-viewer-pane-icon-button-684"
              size="1"
              variant="ghost"
              color="gray"
              onClick={togglePlayPause}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </IconButton>
            <Text size="1" className="tabular-nums text-puntt-neutral-gray-11">
              {formatTimeStamp(videoRef.current?.currentTime ?? 0)} /{" "}
              {formatTimeStamp(duration)}
            </Text>
          </Flex>
          <Flex align="center" gap="2">
            <IconButton
              data-testid="asset-viewer-pane-icon-button-699"
              size="1"
              variant="ghost"
              color="gray"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <SpeakerSimpleSlash size={16} />
              ) : (
                <SpeakerSimpleHigh size={16} />
              )}
            </IconButton>
            <Slider
              variant="classic"
              size="1"
              min={0}
              max={1}
              step={0.05}
              value={[isMuted ? 0 : volume]}
              onValueChange={handleVolumeChange}
              className="w-16 cursor-pointer [--cursor-slider-thumb:pointer]"
            />
            <IconButton
              data-testid="asset-viewer-pane-icon-button-722"
              size="1"
              variant="ghost"
              color="gray"
              onClick={toggleFullscreen}
              aria-label="Fullscreen"
            >
              <CornersOut size={16} />
            </IconButton>
          </Flex>
        </Flex>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// scrollWrapperToTarget / useScrollToTargetEffect
//
// Keeps the active pin and the citation highlight ping visible inside PdfViewer
// and ImageViewer. Pins and ping share the same positioning scheme
// (rel_x/rel_y of wrapperRef), so one helper handles both. Exported for tests.
// ---------------------------------------------------------------------------

export type ScrollTarget = { relX: number; relY: number };

export function scrollWrapperToTarget(
  scrollEl: HTMLDivElement | null,
  wrapperEl: HTMLDivElement | null,
  target: ScrollTarget | null,
  marginPx = 48,
): void {
  if (!scrollEl || !wrapperEl || !target) return;
  const { offsetWidth: ww, offsetHeight: wh } = wrapperEl;
  if (ww === 0 || wh === 0) return;
  const targetX = target.relX * ww;
  const targetY = target.relY * wh;
  const { clientWidth: cw, clientHeight: ch, scrollLeft, scrollTop } = scrollEl;
  const visibleX =
    targetX >= scrollLeft + marginPx && targetX <= scrollLeft + cw - marginPx;
  const visibleY =
    targetY >= scrollTop + marginPx && targetY <= scrollTop + ch - marginPx;
  if (visibleX && visibleY) return;
  scrollEl.scrollTo({
    left: targetX - cw / 2,
    top: targetY - ch / 2,
    behavior: "smooth",
  });
}

function useScrollToTargetEffect(
  ready: boolean,
  target: ScrollTarget | null,
  scrollRef: RefObject<HTMLDivElement | null>,
  wrapperRef: RefObject<HTMLDivElement | null>,
): void {
  const relX = target?.relX ?? null;
  const relY = target?.relY ?? null;
  useEffect(() => {
    if (!ready || relX == null || relY == null) return;
    const id = requestAnimationFrame(() => {
      scrollWrapperToTarget(scrollRef.current, wrapperRef.current, {
        relX,
        relY,
      });
    });
    return () => cancelAnimationFrame(id);
  }, [ready, relX, relY, scrollRef, wrapperRef]);
}

// ---------------------------------------------------------------------------
// PdfViewer -- full-pane renderer with page nav, highlight overlay, and pins
// ---------------------------------------------------------------------------

function PdfViewer({
  url,
  initialPage,
  highlightPosition,
  onPageChange,
  comments,
  activeCommentId,
  onPinClick,
  onPinDragEnd,
}: {
  url: string;
  initialPage?: number;
  highlightPosition?: HighlightPosition | null;
  onPageChange?: (setter: (page: number) => void) => void;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(
    initialPage && initialPage > 0 ? initialPage : 1,
  );
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotations, setRotations] = useState<Record<number, Rotation>>({});
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderGenRef = useRef(0);
  const totalPagesRef = useRef(0);
  totalPagesRef.current = totalPages;

  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPagesRef.current) {
        setCurrentPage(page);
      }
    });
  }, [onPageChange]);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const rotationsRef = useRef(rotations);
  rotationsRef.current = rotations;

  // Navigation: grab-to-pan, center-preserving zoom, pinch-to-zoom, keyboard
  const [isPanning, setIsPanning] = useState(false);
  const pendingAnchorRef = useRef<{
    fx: number;
    fy: number;
    cursorX?: number;
    cursorY?: number;
  } | null>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);
  const panStartRef = useRef<{
    x: number;
    y: number;
    sl: number;
    st: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  const userHasPannedRef = useRef(false);
  const pendingRotationAnchorRef = useRef<{ x: number; y: number } | null>(
    null,
  );

  const VIEWER_PAN_THRESHOLD = 4;

  function handleZoomChange(
    newZoomOrUpdater: number | ((z: number) => number),
  ) {
    const scrollEl = scrollRef.current;
    const canAnchor = scrollEl && scrollEl.scrollWidth > scrollEl.clientWidth;
    if (canAnchor) {
      pendingAnchorRef.current = {
        fx:
          (scrollEl.scrollLeft + scrollEl.clientWidth / 2) /
          scrollEl.scrollWidth,
        fy:
          (scrollEl.scrollTop + scrollEl.clientHeight / 2) /
          scrollEl.scrollHeight,
      };
    }
    setZoom(newZoomOrUpdater);
  }

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const anchor = pendingAnchorRef.current;
    if (!scrollEl || !anchor) return;
    if (anchor.cursorX != null && anchor.cursorY != null) {
      scrollEl.scrollLeft = anchor.fx * scrollEl.scrollWidth - anchor.cursorX;
      scrollEl.scrollTop = anchor.fy * scrollEl.scrollHeight - anchor.cursorY;
    } else {
      scrollEl.scrollLeft =
        anchor.fx * scrollEl.scrollWidth - scrollEl.clientWidth / 2;
      scrollEl.scrollTop =
        anchor.fy * scrollEl.scrollHeight - scrollEl.clientHeight / 2;
    }
    pendingAnchorRef.current = null;
  }, [zoom]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (!cachedRectRef.current) {
        cachedRectRef.current = scrollEl!.getBoundingClientRect();
      }
      const rect = cachedRectRef.current;
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const fx = (scrollEl!.scrollLeft + cx) / scrollEl!.scrollWidth;
      const fy = (scrollEl!.scrollTop + cy) / scrollEl!.scrollHeight;
      const factor = e.deltaY < 0 ? 1.05 : 0.95;
      pendingAnchorRef.current = { fx, fy, cursorX: cx, cursorY: cy };
      setZoom((z) => clampZoom(z * factor));
    }

    scrollEl.addEventListener("wheel", onWheel, { passive: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => scrollEl.removeEventListener("wheel", onWheel);
  }, []);

  // Reset the pan-lock when a new AI citation navigates to a different comment.
  useEffect(() => {
    userHasPannedRef.current = false;
  }, [activeCommentId]);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey) return;
    cachedRectRef.current = null;
    e.currentTarget.setPointerCapture(e.pointerId);
    panMovedRef.current = false;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      sl: scrollRef.current?.scrollLeft ?? 0,
      st: scrollRef.current?.scrollTop ?? 0,
    };
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!panStartRef.current || !scrollRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (!panMovedRef.current && Math.hypot(dx, dy) < VIEWER_PAN_THRESHOLD)
      return;
    panMovedRef.current = true;
    if (!isPanning) setIsPanning(true);
    userHasPannedRef.current = true;
    scrollRef.current.scrollLeft = panStartRef.current.sl - dx;
    scrollRef.current.scrollTop = panStartRef.current.st - dy;
  }

  function handlePointerUp() {
    setIsPanning(false);
    panStartRef.current = null;
    panMovedRef.current = false;
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const PAN_STEP = 50;
    switch (e.key) {
      case "+":
      case "=":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z + ZOOM_STEP));
        break;
      case "-":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z - ZOOM_STEP));
        break;
      case "0":
        e.preventDefault();
        handleZoomChange(1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        scrollEl.scrollLeft -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowRight":
        e.preventDefault();
        scrollEl.scrollLeft += PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowUp":
        e.preventDefault();
        scrollEl.scrollTop -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowDown":
        e.preventDefault();
        scrollEl.scrollTop += PAN_STEP;
        userHasPannedRef.current = true;
        break;
    }
  }

  function handleRotate() {
    if (activeCommentId) {
      userHasPannedRef.current = false;
    } else {
      const scrollEl = scrollRef.current;
      const wrapperEl = wrapperRef.current;
      if (scrollEl && wrapperEl && wrapperEl.offsetWidth > 0) {
        const viewCenterX = scrollEl.scrollLeft + scrollEl.clientWidth / 2;
        const viewCenterY = scrollEl.scrollTop + scrollEl.clientHeight / 2;
        const normX = Math.max(
          0,
          Math.min(
            1,
            (viewCenterX - wrapperEl.offsetLeft) / wrapperEl.offsetWidth,
          ),
        );
        const normY = Math.max(
          0,
          Math.min(
            1,
            (viewCenterY - wrapperEl.offsetTop) / wrapperEl.offsetHeight,
          ),
        );
        pendingRotationAnchorRef.current = inverseRotatePoint(
          { x: normX, y: normY },
          currentPageRotation,
        );
      }
    }
    setRotations((prev) => ({
      ...prev,
      [currentPage]: nextRotation(prev[currentPage] ?? 0),
    }));
  }

  const renderPage = useCallback(async (pageNum: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    // PDF.js cannot render two pages into the same canvas at once. Cancel the
    // in-flight render; track generation so a superseded call can no-op cleanly.
    const myGen = ++renderGenRef.current;
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        // cancel() may throw if the task already settled; safe to ignore.
      }
      renderTaskRef.current = null;
    }

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      if (myGen !== renderGenRef.current) return;

      const scale = 1.5 * zoomRef.current;
      const rotation = rotationsRef.current[pageNum] ?? 0;
      // pdf.js accepts rotation in {0, 90, 180, 270} CW. viewport.width/height
      // swap for 90/270, which we mirror into canvas.width/height below.
      const viewport = page.getViewport({ scale, rotation });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) {
        setError("Failed to render page");
        setRendering(false);
        return;
      }

      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      if (myGen === renderGenRef.current) {
        renderTaskRef.current = null;
        setRendering(false);
        // Canvas is now the rotated size — restore any pending rotation anchor
        const rotAnchor = pendingRotationAnchorRef.current;
        if (rotAnchor) {
          pendingRotationAnchorRef.current = null;
          const scrollEl = scrollRef.current;
          const wrapperEl = wrapperRef.current;
          if (scrollEl && wrapperEl) {
            const r = rotationsRef.current[pageNum] ?? 0;
            const displayPoint = rotatePoint(rotAnchor, r as Rotation);
            scrollEl.scrollLeft = Math.max(
              0,
              wrapperEl.offsetLeft +
                displayPoint.x * wrapperEl.offsetWidth -
                scrollEl.clientWidth / 2,
            );
            scrollEl.scrollTop = Math.max(
              0,
              wrapperEl.offsetTop +
                displayPoint.y * wrapperEl.offsetHeight -
                scrollEl.clientHeight / 2,
            );
          }
        }
      }
    } catch (err) {
      // A superseded render rejects with RenderingCancelledException; expected.
      if (
        (err as { name?: string } | null)?.name ===
        "RenderingCancelledException"
      ) {
        return;
      }
      console.error("Error rendering PDF page:", err);
      if (myGen === renderGenRef.current) {
        setError("Failed to render page");
        setRendering(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      try {
        const signedUrl = await getSignedPdfUrl(url);
        if (cancelled) return;
        const pdfDoc = await getDocument(signedUrl).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        // Respect any page set before the PDF finished loading (e.g. a
        // cross-file citation that seeded currentPage via initialPage).
        const startPage = Math.min(
          Math.max(1, currentPageRef.current || 1),
          pdfDoc.numPages,
        );
        if (startPage !== currentPageRef.current) {
          setCurrentPage(startPage);
        }
        await renderPage(startPage);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading PDF:", err);
        const isPasswordProtected =
          (err as { name?: string })?.name === "PasswordException";
        setError(
          isPasswordProtected
            ? "This PDF is password protected and cannot be displayed. Download it to view."
            : "Failed to load PDF",
        );
        setRendering(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
      // Bump generation so any renderPage still suspended in getPage() short-
      // circuits after resume instead of touching a detached canvas/state.
      renderGenRef.current++;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
        renderTaskRef.current = null;
      }
    };
  }, [url, renderPage]);

  const currentPageRotation = rotations[currentPage] ?? 0;
  useEffect(() => {
    if (pdfDocRef.current && currentPage > 0) {
      renderPage(currentPage);
    }
  }, [currentPage, zoom, currentPageRotation, renderPage]);

  useEffect(() => {
    if (highlightPosition?.page && highlightPosition.page !== currentPage) {
      setCurrentPage(highlightPosition.page);
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [url]);

  const rotatedHighlight = useMemo(
    () =>
      highlightPosition
        ? rotatePoint(
            { x: highlightPosition.x, y: highlightPosition.y },
            currentPageRotation,
          )
        : null,
    [highlightPosition, currentPageRotation],
  );

  // Center the active pin / citation highlight in view when selection changes.
  // Targets are memoized so the effect only fires when the actual coordinates
  // change, not on every poll that refreshes the `comments` array reference.
  // Gated on `!rendering` so wrapperRef.offsetHeight reflects the new page.
  // Pins and ping render in displayed (rotated) space; apply `rotatePoint` so
  // the scroll lands where the target actually appears on screen.
  const activePinTarget = useMemo<ScrollTarget | null>(() => {
    if (!activeCommentId) return null;
    const pin = (comments ?? []).find((c) => c._id === activeCommentId);
    if (!pin || (pin.page_num ?? 1) !== currentPage) return null;
    if (pin.rel_x == null || pin.rel_y == null) return null;
    const r = rotatePoint({ x: pin.rel_x, y: pin.rel_y }, currentPageRotation);
    return { relX: r.x, relY: r.y };
  }, [activeCommentId, comments, currentPage, currentPageRotation]);

  const highlightTarget = useMemo<ScrollTarget | null>(() => {
    if (!highlightPosition || !rotatedHighlight) return null;
    if (highlightPosition.page && highlightPosition.page !== currentPage)
      return null;
    return { relX: rotatedHighlight.x, relY: rotatedHighlight.y };
  }, [highlightPosition, rotatedHighlight, currentPage]);

  useScrollToTargetEffect(
    !rendering && !userHasPannedRef.current,
    activePinTarget,
    scrollRef,
    wrapperRef,
  );
  useScrollToTargetEffect(
    !rendering && !userHasPannedRef.current,
    highlightTarget,
    scrollRef,
    wrapperRef,
  );

  const pageComments = (comments ?? []).filter(
    (c) => (c.page_num ?? 1) === currentPage,
  );

  // Surface backend bugs where a comment lands at the renderer with no
  // page_num. The filter above defaults to page 1 — without this warning
  // every orphan pin would silently render on page 1 with no signal that
  // page was unknown. Log once per comment id per session.
  useOrphanPagePinWarning(comments);

  if (error) {
    const isPasswordError = error.includes("password protected");
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-xs flex-col items-center gap-3 text-center">
          {isPasswordError ? (
            <Lock size={32} className="text-puntt-neutral-gray-9" />
          ) : null}
          <Text size="2" className="text-puntt-neutral-gray-11" weight="medium">
            {isPasswordError ? "Password protected PDF" : "Failed to load PDF"}
          </Text>
          <Text size="1" className="text-puntt-neutral-gray-9">
            {isPasswordError
              ? "This file is encrypted. Download it and open with your PDF viewer to enter the password."
              : error}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        data-testid="asset-viewer-pane-div-1284"
        ref={scrollRef}
        role="application"
        aria-label="Document viewer"
        tabIndex={0}
        className={`relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2 outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9 ${
          isPanning ? "cursor-grabbing select-none" : "cursor-grab"
        }`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={handleKeyDown}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        {rendering && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3/80">
            <Spinner size="2" />
          </div>
        )}
        <div
          ref={wrapperRef}
          className="relative mx-auto"
          style={{ width: `${zoom * 100}%` }}
        >
          <canvas ref={canvasRef} className="block w-full" />
          {pageComments.map((comment) => (
            <ImageCommentPin
              key={comment._id}
              comment={comment}
              isActive={comment._id === activeCommentId}
              dimmed={!!activeCommentId && comment._id !== activeCommentId}
              onPinClick={(c) => onPinClick?.(c)}
              onDragEnd={onPinDragEnd}
              containerRef={wrapperRef}
              rotation={currentPageRotation}
            />
          ))}
          {rotatedHighlight &&
            highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentPage) && (
              <div
                className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full"
                style={{
                  left: `${rotatedHighlight.x * 100}%`,
                  top: `${rotatedHighlight.y * 100}%`,
                }}
                data-testid="asset-viewer-citation-highlight"
                role="presentation"
                aria-hidden="true"
              >
                {/* Steady, high-contrast ring — stays visible for the
                    full highlight window so the user has time to notice
                    it on a busy spec PDF. Nestle CSV #11 feedback: prior
                    single `animate-ping` element was too brief to spot
                    once it had collapsed back to scale 1 / opacity 0. */}
                <div className="size-12 rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/25 shadow-[0_0_0_3px_rgba(255,255,255,0.7)]" />
                {/* Layered animated ping on top of the ring for an
                    initial attention-grab; the ring underneath remains
                    after each pulse cycle so the highlight never blinks
                    out between cycles. */}
                <div className="absolute inset-0 size-12 animate-ping rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/40" />
              </div>
            )}
        </div>
      </div>

      <ViewerToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        zoom={zoom}
        rotation={currentPageRotation}
        onPageChange={setCurrentPage}
        onZoomChange={handleZoomChange}
        onRotate={handleRotate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageViewer -- image gallery with highlight overlay, comment pins, and zoom
// ---------------------------------------------------------------------------

function ImageViewer({
  assets,
  highlightPosition,
  comments,
  activeCommentId,
  onPinClick,
  onPinDragEnd,
  onPageChange,
  initialPage,
}: {
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onPageChange?: (setter: (page: number) => void) => void;
  initialPage?: number;
}) {
  // Translate 1-based page → 0-based index. Clamp to assets length so a
  // stale auto-jump request can't index past the array.
  const initialIndex =
    initialPage && initialPage > 0
      ? Math.min(initialPage - 1, Math.max(0, assets.length - 1))
      : 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [imgReady, setImgReady] = useState(false);
  const [rotations, setRotations] = useState<Record<number, Rotation>>({});
  const [naturalDims, setNaturalDims] = useState<
    Record<number, { w: number; h: number }>
  >({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const totalPages = assets.length;
  const asset = assets[currentIndex] ?? assets[0];
  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;

  // Navigation: grab-to-pan, center-preserving zoom, pinch-to-zoom, keyboard
  const [isPanning, setIsPanning] = useState(false);
  const pendingAnchorRef = useRef<{
    fx: number;
    fy: number;
    cursorX?: number;
    cursorY?: number;
  } | null>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);
  const panStartRef = useRef<{
    x: number;
    y: number;
    sl: number;
    st: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  const userHasPannedRef = useRef(false);
  const pendingRotationAnchorRef = useRef<{ x: number; y: number } | null>(
    null,
  );

  const VIEWER_PAN_THRESHOLD = 4;

  function handleZoomChange(
    newZoomOrUpdater: number | ((z: number) => number),
  ) {
    const scrollEl = scrollRef.current;
    const canAnchor =
      scrollEl && scrollEl.scrollWidth > scrollEl.clientWidth && imgReady;
    if (canAnchor) {
      pendingAnchorRef.current = {
        fx:
          (scrollEl.scrollLeft + scrollEl.clientWidth / 2) /
          scrollEl.scrollWidth,
        fy:
          (scrollEl.scrollTop + scrollEl.clientHeight / 2) /
          scrollEl.scrollHeight,
      };
    }
    setZoom(newZoomOrUpdater);
  }

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const anchor = pendingAnchorRef.current;
    if (!scrollEl || !anchor) return;
    if (anchor.cursorX != null && anchor.cursorY != null) {
      scrollEl.scrollLeft = anchor.fx * scrollEl.scrollWidth - anchor.cursorX;
      scrollEl.scrollTop = anchor.fy * scrollEl.scrollHeight - anchor.cursorY;
    } else {
      scrollEl.scrollLeft =
        anchor.fx * scrollEl.scrollWidth - scrollEl.clientWidth / 2;
      scrollEl.scrollTop =
        anchor.fy * scrollEl.scrollHeight - scrollEl.clientHeight / 2;
    }
    pendingAnchorRef.current = null;
  }, [zoom]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (!cachedRectRef.current) {
        cachedRectRef.current = scrollEl!.getBoundingClientRect();
      }
      const rect = cachedRectRef.current;
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const fx = (scrollEl!.scrollLeft + cx) / scrollEl!.scrollWidth;
      const fy = (scrollEl!.scrollTop + cy) / scrollEl!.scrollHeight;
      const factor = e.deltaY < 0 ? 1.05 : 0.95;
      pendingAnchorRef.current = { fx, fy, cursorX: cx, cursorY: cy };
      setZoom((z) => clampZoom(z * factor));
    }

    scrollEl.addEventListener("wheel", onWheel, { passive: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => scrollEl.removeEventListener("wheel", onWheel);
  }, []);

  // Reset the pan-lock when a new AI citation navigates to a different comment.
  useEffect(() => {
    userHasPannedRef.current = false;
  }, [activeCommentId]);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey) return;
    cachedRectRef.current = null;
    e.currentTarget.setPointerCapture(e.pointerId);
    panMovedRef.current = false;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      sl: scrollRef.current?.scrollLeft ?? 0,
      st: scrollRef.current?.scrollTop ?? 0,
    };
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!panStartRef.current || !scrollRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (!panMovedRef.current && Math.hypot(dx, dy) < VIEWER_PAN_THRESHOLD)
      return;
    panMovedRef.current = true;
    if (!isPanning) setIsPanning(true);
    userHasPannedRef.current = true;
    scrollRef.current.scrollLeft = panStartRef.current.sl - dx;
    scrollRef.current.scrollTop = panStartRef.current.st - dy;
  }

  function handlePointerUp() {
    setIsPanning(false);
    panStartRef.current = null;
    panMovedRef.current = false;
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const PAN_STEP = 50;
    switch (e.key) {
      case "+":
      case "=":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z + ZOOM_STEP));
        break;
      case "-":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z - ZOOM_STEP));
        break;
      case "0":
        e.preventDefault();
        handleZoomChange(1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        scrollEl.scrollLeft -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowRight":
        e.preventDefault();
        scrollEl.scrollLeft += PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowUp":
        e.preventDefault();
        scrollEl.scrollTop -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowDown":
        e.preventDefault();
        scrollEl.scrollTop += PAN_STEP;
        userHasPannedRef.current = true;
        break;
    }
  }

  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPagesRef.current) {
        setCurrentIndex(page - 1);
      }
    });
  }, [onPageChange]);

  useEffect(() => {
    if (
      highlightPosition?.page &&
      highlightPosition.page !== currentIndex + 1
    ) {
      setCurrentIndex(
        Math.min(Math.max(highlightPosition.page - 1, 0), totalPages - 1),
      );
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scroll only when the *displayed image source* actually changes.
  // Keying on the `assets` array identity (which can change on every poll
  // because TanStack Query returns a new array reference) was causing the
  // viewport to snap back to the top mid-scroll, preventing users from
  // reaching comments and pins below the fold. Do NOT add rotation to this
  // dep list — rotating should preserve scroll position.
  const currentSource = asset?.source;
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [currentSource]);

  // Reset the load gate when the displayed image changes. onLoad on the <img>
  // flips it back to true once pixels are available.
  useEffect(() => {
    setImgReady(false);
  }, [currentSource]);

  // Cached images fire their browser `load` event before React attaches the
  // onLoad listener, so onLoad never runs. Check `complete` on mount (and
  // after the source changes) to cover that case.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setImgReady(true);
    }
  }, [currentSource]);

  const rotation: Rotation = rotations[currentIndex] ?? 0;
  const dims = naturalDims[currentIndex];
  // Wrapper reflects the displayed (post-rotation) aspect so pin overlays
  // percentage-position against the rotated frame.
  const wrapperAspect = dims
    ? rotation % 180 === 0
      ? `${dims.w} / ${dims.h}`
      : `${dims.h} / ${dims.w}`
    : undefined;
  const rotatedHighlight = useMemo(
    () =>
      highlightPosition
        ? rotatePoint(
            { x: highlightPosition.x, y: highlightPosition.y },
            rotation,
          )
        : null,
    [highlightPosition, rotation],
  );

  // Memoized scroll targets. Pins and ping are positioned in displayed
  // (rotated) space within the wrapper, so we apply `rotatePoint` to land
  // the scroll at the pin's actual on-screen location.
  const activePinTarget = useMemo<ScrollTarget | null>(() => {
    if (!activeCommentId) return null;
    const pin = (comments ?? []).find((c) => c._id === activeCommentId);
    if (!pin || (pin.page_num ?? 1) !== currentIndex + 1) return null;
    if (pin.rel_x == null || pin.rel_y == null) return null;
    const r = rotatePoint({ x: pin.rel_x, y: pin.rel_y }, rotation);
    return { relX: r.x, relY: r.y };
  }, [activeCommentId, comments, currentIndex, rotation]);

  const highlightTarget = useMemo<ScrollTarget | null>(() => {
    if (!highlightPosition || !rotatedHighlight) return null;
    if (highlightPosition.page && highlightPosition.page !== currentIndex + 1)
      return null;
    return { relX: rotatedHighlight.x, relY: rotatedHighlight.y };
  }, [highlightPosition, rotatedHighlight, currentIndex]);

  useLayoutEffect(() => {
    const anchor = pendingRotationAnchorRef.current;
    if (!anchor) return;
    pendingRotationAnchorRef.current = null;
    const scrollEl = scrollRef.current;
    const wrapperEl = wrapperRef.current;
    if (!scrollEl || !wrapperEl) return;
    const displayPoint = rotatePoint(anchor, rotation);
    scrollEl.scrollLeft = Math.max(
      0,
      wrapperEl.offsetLeft +
        displayPoint.x * wrapperEl.offsetWidth -
        scrollEl.clientWidth / 2,
    );
    scrollEl.scrollTop = Math.max(
      0,
      wrapperEl.offsetTop +
        displayPoint.y * wrapperEl.offsetHeight -
        scrollEl.clientHeight / 2,
    );
  }, [rotation]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleRotate() {
    if (activeCommentId) {
      userHasPannedRef.current = false;
    } else {
      const scrollEl = scrollRef.current;
      const wrapperEl = wrapperRef.current;
      if (scrollEl && wrapperEl && wrapperEl.offsetWidth > 0) {
        const viewCenterX = scrollEl.scrollLeft + scrollEl.clientWidth / 2;
        const viewCenterY = scrollEl.scrollTop + scrollEl.clientHeight / 2;
        const normX = Math.max(
          0,
          Math.min(
            1,
            (viewCenterX - wrapperEl.offsetLeft) / wrapperEl.offsetWidth,
          ),
        );
        const normY = Math.max(
          0,
          Math.min(
            1,
            (viewCenterY - wrapperEl.offsetTop) / wrapperEl.offsetHeight,
          ),
        );
        pendingRotationAnchorRef.current = inverseRotatePoint(
          { x: normX, y: normY },
          rotation,
        );
      }
    }
    setRotations((prev) => ({
      ...prev,
      [currentIndex]: nextRotation(prev[currentIndex] ?? 0),
    }));
  }

  useScrollToTargetEffect(
    imgReady && !userHasPannedRef.current,
    activePinTarget,
    scrollRef,
    wrapperRef,
  );
  useScrollToTargetEffect(
    imgReady && !userHasPannedRef.current,
    highlightTarget,
    scrollRef,
    wrapperRef,
  );

  const pageComments = (comments ?? []).filter(
    (c) => (c.page_num ?? 1) === currentIndex + 1,
  );

  useOrphanPagePinWarning(comments);

  return (
    <div className="flex h-full flex-col">
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        data-testid="asset-viewer-pane-div-1725"
        ref={scrollRef}
        role="application"
        aria-label="Image viewer"
        tabIndex={0}
        className={`relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2 outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9 ${
          isPanning ? "cursor-grabbing select-none" : "cursor-grab"
        }`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={handleKeyDown}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          ref={wrapperRef}
          className="relative mx-auto"
          style={{
            width: `${zoom * 100}%`,
            aspectRatio: wrapperAspect,
          }}
        >
          <img
            ref={imgRef}
            src={asset.source}
            alt={asset.name ?? `Image ${currentIndex + 1}`}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setNaturalDims((prev) => ({
                  ...prev,
                  [currentIndex]: {
                    w: img.naturalWidth,
                    h: img.naturalHeight,
                  },
                }));
              }
              setImgReady(true);
            }}
            className="absolute left-1/2 top-1/2 block"
            style={{
              // At 0°/180° the image fits the wrapper width; at 90°/270° the
              // aspect flip means we fit its long side to the wrapper height
              // instead. transform-origin center keeps it visually anchored.
              width: rotation % 180 === 0 ? "100%" : "auto",
              height: rotation % 180 === 0 ? "auto" : "100%",
              maxWidth: rotation % 180 === 0 ? "100%" : "none",
              maxHeight: "100%",
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              transformOrigin: "center",
            }}
          />
          {pageComments.map((comment) => (
            <ImageCommentPin
              key={comment._id}
              comment={comment}
              isActive={comment._id === activeCommentId}
              dimmed={!!activeCommentId && comment._id !== activeCommentId}
              onPinClick={(c) => onPinClick?.(c)}
              onDragEnd={onPinDragEnd}
              containerRef={wrapperRef}
              rotation={rotation}
            />
          ))}
          {rotatedHighlight &&
            highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentIndex + 1) && (
              <div
                className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full"
                style={{
                  left: `${rotatedHighlight.x * 100}%`,
                  top: `${rotatedHighlight.y * 100}%`,
                }}
                data-testid="asset-viewer-citation-highlight"
                role="presentation"
                aria-hidden="true"
              >
                {/* Steady, high-contrast ring + animated ping — see the
                    matching PdfViewer block above for rationale (Nestle
                    CSV #11). Same shape so the highlight feels
                    consistent across image and PDF viewers. */}
                <div className="size-12 rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/25 shadow-[0_0_0_3px_rgba(255,255,255,0.7)]" />
                <div className="absolute inset-0 size-12 animate-ping rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/40" />
              </div>
            )}
        </div>
      </div>

      <ViewerToolbar
        currentPage={currentIndex + 1}
        totalPages={totalPages}
        zoom={zoom}
        rotation={rotation}
        onPageChange={(page) => setCurrentIndex(page - 1)}
        onZoomChange={handleZoomChange}
        onRotate={handleRotate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared toolbar for pagination + zoom (DRYs up PdfViewer / ImageViewer)
// ---------------------------------------------------------------------------

function ViewerToolbar({
  currentPage,
  totalPages,
  zoom,
  rotation,
  onPageChange,
  onZoomChange,
  onRotate,
}: {
  currentPage: number;
  totalPages: number;
  zoom: number;
  rotation?: Rotation;
  onPageChange: (page: number) => void;
  onZoomChange: (zoom: number | ((z: number) => number)) => void;
  onRotate?: () => void;
}) {
  return (
    <Flex
      align="center"
      justify="between"
      className="h-10 shrink-0 border-t border-puntt-neutral-gray-6 bg-base-white px-3"
    >
      {totalPages > 1 ? (
        <Flex align="center" gap="2">
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            data-testid="asset-viewer-prev-page"
            aria-label="Previous page"
          >
            <CaretLeft size={14} />
          </IconButton>
          <Text
            size="1"
            className="tabular-nums text-puntt-neutral-gray-11"
            data-testid="asset-viewer-page-indicator"
          >
            Page {currentPage} of {totalPages}
          </Text>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            data-testid="asset-viewer-next-page"
            aria-label="Next page"
          >
            <CaretRight size={14} />
          </IconButton>
        </Flex>
      ) : (
        <span />
      )}
      <Flex align="center" gap="1">
        {onRotate && (
          <Button
            size="1"
            variant="ghost"
            color="gray"
            onClick={onRotate}
            aria-label="Rotate 90°"
            title={`Rotate 90° (currently ${rotation ?? 0}°)`}
            data-testid="asset-viewer-rotate"
            className="cursor-pointer"
          >
            <ArrowClockwise size={14} />
            <Text as="span" size="1">
              Rotate
            </Text>
          </Button>
        )}
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => onZoomChange((z: number) => clampZoom(z - ZOOM_STEP))}
          aria-label="Zoom out"
          data-testid="asset-viewer-zoom-out"
        >
          <MagnifyingGlassMinus size={14} />
        </IconButton>
        <Button
          size="1"
          variant="ghost"
          color="gray"
          onClick={() => onZoomChange(1)}
          aria-label="Reset zoom"
          data-testid="asset-viewer-zoom-reset"
          className="min-w-12 cursor-pointer tabular-nums"
        >
          {Math.round(zoom * 100)}%
        </Button>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => onZoomChange((z: number) => clampZoom(z + ZOOM_STEP))}
          aria-label="Zoom in"
          data-testid="asset-viewer-zoom-in"
        >
          <MagnifyingGlassPlus size={14} />
        </IconButton>
      </Flex>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// AssetViewerPane -- main exported component
// ---------------------------------------------------------------------------

export const AssetViewerPane = forwardRef<
  AssetViewerPaneHandle,
  AssetViewerPaneProps
>(function AssetViewerPane(
  {
    title,
    assets,
    highlightPosition,
    reviewStatus,
    comments,
    activeCommentId,
    onCommentClick,
    onPinClick,
    onCitationClick,
    onPinDragEnd,
    onCollapse,
    onBackToProject,
    ticketId,
  },
  ref,
) {
  const [activeTab, setActiveTab] = useState(0);
  // State (not ref) so the navigation effect re-runs when the inner viewer
  // registers its page setter. A ref would silently drop the first navigation
  // request when comments arrive before the PDF has finished loading.
  const [pageNavigator, setPageNavigator] = useState<
    ((page: number) => void) | null
  >(null);
  const pageNavigatorRef = useRef<((page: number) => void) | null>(null);
  pageNavigatorRef.current = pageNavigator;
  // Stable handler so the inner viewer's `useEffect(..., [onPageChange])`
  // doesn't refire every parent render. A fresh inline arrow would loop:
  // effect fires → setPageNavigator → parent re-renders → new prop ref →
  // effect fires again, hitting React's max-update-depth.
  const registerPageNavigator = useCallback(
    (setter: (page: number) => void) => setPageNavigator(() => setter),
    [],
  );
  // Page to apply on the newly-activated viewer once it mounts. The viewer
  // reads it as `initialPage`; setting it synchronously via a setter after
  // setActiveTab would hit the outgoing viewer (or a not-yet-loaded new one).
  const pendingPageRef = useRef<number | null>(null);
  // Sticky pointer to the first Office file we've seen on this mount. The
  // review pipeline rasterizes spreadsheets to PNG pages for layout analysis,
  // which would otherwise swap the Microsoft Office iframe for an image
  // viewer mid-session — a jarring switch the user can't undo. Holding onto
  // the original URL lets us keep the office viewer rendered after the
  // backend replaces ``reviewFiles`` with rasterized derivatives. Reset by
  // remounting (chat.tsx keys this pane on the active ticket id).
  const [officeSnapshot, setOfficeSnapshot] = useState<AssetFile | null>(null);

  // Page to seed into the viewer on its very first mount, derived from the
  // first auto-selected comment. Without this, comments that arrive before
  // the PDF loads lose their navigation request — the effect below fires
  // once with no setter registered, then never re-runs for that activeId.
  const initialActiveCommentPage = useMemo<number | null>(() => {
    if (!activeCommentId || !comments?.length) return null;
    const comment = comments.find((c) => c._id === activeCommentId);
    const page = comment?.page_num;
    return typeof page === "number" && page > 0 ? page : null;
  }, [activeCommentId, comments]);

  useImperativeHandle(ref, () => ({
    navigateToPage(page: number) {
      pageNavigatorRef.current?.(page);
    },
    navigateToFile(fileIndex: number, pendingPage?: number) {
      if (fileIndex < 0 || fileIndex >= assets.length) return;
      if (fileIndex === activeTab) {
        // Same file — viewer is already mounted; page directly via the
        // live setter.
        if (pendingPage != null) pageNavigatorRef.current?.(pendingPage);
        return;
      }
      if (pendingPage != null) pendingPageRef.current = pendingPage;
      setActiveTab(fileIndex);
    },
  }));

  // Once the newly-activated viewer has rendered with `initialPage`, clear
  // the pending ref so manual tab clicks don't reuse a stale page.
  useEffect(() => {
    pendingPageRef.current = null;
  }, [activeTab]);

  // Navigate to the active comment's page. Depends on `pageNavigator` (state)
  // so it re-fires when the inner viewer finishes loading and registers its
  // setter — covering the race where comments arrive before the PDF parses.
  useEffect(() => {
    if (!activeCommentId || !comments?.length || !pageNavigator) return;
    const comment = comments.find((c) => c._id === activeCommentId);
    if (comment?.page_num != null) {
      pageNavigator(comment.page_num);
    }
  }, [activeCommentId, comments, pageNavigator]);

  // Capture and update the Office snapshot in lockstep with `assets`. The
  // common transition we care about is ``[docx]`` → ``[png, png, ...]`` — the
  // rasterized derivatives are pure images, so we hold onto the snapshot.
  // (Spreadsheets bypass this snapshot entirely — they route through PdfViewer.)
  // When the next `assets` arrives carrying a *different* Office file we
  // replace the snapshot, and when it arrives with a non-image, non-Office
  // asset (PDF / video / unrecognized) we drop it so the dispatch can route
  // to the correct viewer.
  useEffect(() => {
    const newOfficeAsset = assets.find(isOfficeFile);
    if (newOfficeAsset) {
      if (newOfficeAsset.source !== officeSnapshot?.source) {
        setOfficeSnapshot(newOfficeAsset);
      }
      return;
    }
    if (assets.length === 0) return;
    const allImagesNow = assets.every((a) => isImage(a));
    if (!allImagesNow && officeSnapshot) {
      setOfficeSnapshot(null);
    }
  }, [assets, officeSnapshot]);

  if (assets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          No {title.toLowerCase()} files
        </Text>
      </div>
    );
  }

  const currentAsset = assets[activeTab] ?? assets[0];
  const allImages = assets.length > 1 && assets.every((a) => isImage(a));
  // Once we've snapshotted an Office file, keep showing its viewer whenever
  // the live assets have collapsed into pure images (rasterized pages from
  // the office-to-pdf-to-png pipeline). Without this guard the Microsoft
  // iframe would silently swap for the image viewer mid-review.
  const useOfficeSnapshot =
    officeSnapshot != null && assets.every((a) => isImage(a));
  const showTabs = assets.length > 1 && !allImages && !useOfficeSnapshot;
  const hasComments = comments && comments.length > 0;

  // When this pane is embedded inside a view that already renders its own
  // title chrome (e.g. TicketViewPane), the caller passes `title=""` and no
  // back/collapse/status/tabs. In that case our own header collapses into
  // an empty strip under the outer title — hide it entirely.
  const showHeader = Boolean(
    title || onBackToProject || onCollapse || reviewStatus || showTabs,
  );

  return (
    <div
      data-testid="asset-viewer-pane"
      className="flex h-full flex-col bg-base-white"
    >
      {/* Header */}
      {showHeader && (
        <div
          data-testid="asset-viewer-header"
          className={`shrink-0 border-b border-puntt-neutral-gray-6 px-4 ${
            showTabs || reviewStatus ? "py-2" : "flex h-10 items-center"
          }`}
        >
          <Flex
            align="center"
            gap="2"
            className={showTabs ? "mb-1 w-full" : "w-full"}
          >
            {onBackToProject && (
              <button
                onClick={onBackToProject}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-puntt-accent-9 transition-colors hover:bg-puntt-accent-2"
                aria-label="Back to project"
                data-testid="back-to-project"
              >
                <ArrowLeft size={12} weight="bold" />
                <Text size="1" weight="medium">
                  Project
                </Text>
              </button>
            )}
            <Text
              size="1"
              weight="medium"
              className="uppercase tracking-wide text-puntt-neutral-gray-9"
            >
              {title}
            </Text>
            {onCollapse && (
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                className="ml-auto"
                aria-label={`Collapse ${title} pane`}
                data-testid="asset-viewer-collapse"
                onClick={onCollapse}
              >
                <ArrowsInSimple size={14} />
              </IconButton>
            )}
          </Flex>
          {reviewStatus && (
            <Text
              size="1"
              className={`italic ${
                reviewStatus === "Review complete"
                  ? "text-malachite-700"
                  : "animate-pulse text-puntt-accent-9"
              }`}
            >
              {reviewStatus}
            </Text>
          )}
          {showTabs && (
            <Flex gap="1" className="overflow-x-auto">
              {assets.map((asset, i) => (
                <button
                  key={i}
                  data-testid="asset-viewer-tab"
                  onClick={() => setActiveTab(i)}
                  className={`rounded px-2 py-0.5 transition-colors ${
                    i === activeTab
                      ? "bg-puntt-accent-3 text-puntt-accent-11"
                      : "text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-3"
                  }`}
                >
                  <Text
                    as="span"
                    size="1"
                    weight={i === activeTab ? "medium" : undefined}
                  >
                    {asset.name ?? `File ${i + 1}`}
                  </Text>
                </button>
              ))}
            </Flex>
          )}
        </div>
      )}

      {/* Content */}
      <div className="min-h-0 flex-1">
        {useOfficeSnapshot ? (
          <OfficeViewer url={officeSnapshot!.source} />
        ) : allImages ? (
          <ImageViewer
            assets={assets}
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
            initialPage={
              pendingPageRef.current ?? initialActiveCommentPage ?? undefined
            }
          />
        ) : isPdf(currentAsset) || isSpreadsheet(currentAsset) ? (
          <PdfViewer
            key={currentAsset.source}
            url={currentAsset.source}
            initialPage={
              pendingPageRef.current ?? initialActiveCommentPage ?? undefined
            }
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        ) : isImage(currentAsset) ? (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        ) : isVideo(currentAsset) ? (
          <VideoViewer
            url={currentAsset.source}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            reviewStatus={reviewStatus}
          />
        ) : isOfficeFile(currentAsset) ? (
          <OfficeViewer url={currentAsset.source} />
        ) : (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={onPinDragEnd}
          />
        )}
      </div>

      {/* Comment panel */}
      {hasComments && (
        <CommentPanel
          comments={comments}
          activeCommentId={activeCommentId}
          onCommentClick={onCommentClick}
          onCitationClick={onCitationClick}
          onNavigateToPage={(page) => pageNavigatorRef.current?.(page)}
          ticketId={ticketId}
        />
      )}
    </div>
  );
});
